import { useRef } from 'react';
import { formatDollar, formatLargeDollar, formatNumber } from '../../lib/formatters';
import { Gwei } from '../../lib/gwei';
import { multiply } from '../../lib/math';
import { Button } from '../catalyst-ui-kit/button';
import { Input } from '../catalyst-ui-kit/input';
import { IconSelect } from '../IconSelect';
import { TokenName } from '../IconSelect/IconSelect';
import { CustomNumericFormat } from '../Input/CustomNumericFormat';
import { SelectOption } from '../MultiSelect/MultiSelect';

export const SelectableTokenInput = <T extends TokenName>({
  amount,
  selectedToken,
  options,
  tokenPrice,
  balance,
  hasError,
  disabled,
  setAmount,
  setSelectedToken,
  balanceLabel = 'Balance',
  setMax,
  autoFocus,
}: {
  amount: string | undefined;
  selectedToken: SelectOption<T> | undefined;
  options: SelectOption<T>[];
  tokenPrice: bigint | undefined;
  balance: bigint | undefined;
  balanceLabel?: string;
  hasError?: boolean;
  disabled?: boolean;
  setSelectedToken: (token: SelectOption<T> | undefined) => void;
  setAmount: (amount: string) => void;
  setMax?: () => void;
  autoFocus?: boolean;
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const amountValue =
    amount !== undefined && tokenPrice !== undefined
      ? multiply(Gwei.toGwei(amount), tokenPrice)
      : 0n;

  return (
    <Input hasError={hasError} disabled={disabled} containerClassName="h-auto grow">
      <div className="flex flex-col gap-1">
        <div className="flex items-center justify-between">
          <CustomNumericFormat
            id="deposit"
            disabled={disabled}
            className="flex-1 grow bg-transparent !p-0 text-sm placeholder:text-contentTertiary"
            value={amount}
            aria-label="Amount"
            autoFocus={autoFocus}
            getInputRef={inputRef}
            onValueChange={(values) => {
              setAmount(values.value);
            }}
          />
          <IconSelect
            options={options}
            value={selectedToken?.value}
            onChange={setSelectedToken}
            disabled={disabled}
          />
        </div>
        <div className="flex items-center justify-between text-3xs">
          <div className="text-3xs text-contentSecondary" aria-label="USD Value">
            {amountValue > Gwei.toGwei('1000000')
              ? formatLargeDollar(amountValue)
              : formatDollar(amountValue)}
          </div>
          <div className="flex items-center gap-1">
            <p className="text-nowrap font-medium text-contentSecondary">
              {balanceLabel}: {balance !== undefined ? formatNumber(balance) : '-'}
            </p>
            {setMax ? (
              <Button
                text
                size="2xs"
                aria-label="Max"
                disabled={disabled || amountValue === (balance || 0n)}
                onClick={() => {
                  setMax();
                  inputRef.current?.focus();
                }}
              >
                Max
              </Button>
            ) : null}
          </div>
        </div>
      </div>
    </Input>
  );
};
